import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, IconButton, styled, Tooltip } from '@mui/material';

import { Icon, Select, TextField } from '@components/base';
import FilterDefinition, { SearchFilterScope, searchFilterScope } from '@models/FilterDefinition';
import { FilterField, FilterGroup } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

import FilterAutocompleteField from './FilterAutocompleteField';
import FilterBooleanField from './FilterBooleanField';
import FilterDateInput from './FilterDateInput';
import FilterList from './FilterList';

export const columnCount = 5;

const CollectionFilterList = styled(Box)`
  grid-column: 1 / ${columnCount + 1};
  grid-row: 2;
  width: 1fr;
`;

const StyledAutocomplete = styled(Autocomplete)`
  flex-grow: 1;
  min-width: 200px;
`;

type FilterInputsProps = {
  filterDefinition: FilterDefinition;
  filterDefinitionPath?: FilterDefinition[];
  filterField: FilterField;
  nestingLevel: number;
  onChange: (filterField: FilterField) => void;
  onRemove?: () => void;
};

const FilterInputs: FC<FilterInputsProps> = ({
  filterDefinition,
  filterDefinitionPath = [],
  filterDefinition: { canAutocomplete, operators, type, tokens, fields, propertyName, displayName },
  filterField,
  nestingLevel,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const { isReadonly } = useSearchAndFilter();

  const handleOnFiltersChange = useCallback(
    (filter: FilterGroup) => onChange({ ...filterField, filter }),
    [filterField, onChange]
  );

  const valuePlaceholder =
    filterField.operator === 'search.in' ? 'value1, value2, value3' : 'value';

  const hasSubGroup = (type === 'collection' || type === 'object') && fields.length > 0;

  const fieldName = [...filterDefinitionPath.map(fd => fd.propertyName), propertyName].join('/');

  return (
    <>
      {operators.length > 0 && type !== 'boolean' && (
        <Select
          disabled={isReadonly}
          label={
            operators.some(operator => !searchFilterScope.includes(operator as SearchFilterScope))
              ? t('operator')
              : t('scope')
          }
          onChange={({ currentTarget: { value } }) =>
            onChange({ ...filterField, operator: value as FilterField['operator'] })
          }
          options={operators.map(operator => ({
            label: t('oData.operator', { context: operator }),
            value: operator,
          }))}
          value={filterField.operator ?? -1}
        />
      )}

      {!hasSubGroup &&
        (canAutocomplete ? (
          <FilterAutocompleteField
            fieldName={fieldName}
            setValue={value => onChange({ ...filterField, value })}
            value={(filterField.value as string) ?? ''}
          />
        ) : tokens?.length > 0 ? (
          type === 'dateTime' ? (
            <FilterDateInput
              filterDefinition={filterDefinition}
              setValue={value => onChange({ ...filterField, value })}
              value={(filterField.value as string) ?? ''}
            />
          ) : (
            <StyledAutocomplete
              autoComplete
              autoHighlight
              autoSelect
              disabled={isReadonly}
              onChange={(_, value) =>
                onChange({ ...filterField, value: typeof value === 'string' ? value : '' })
              }
              options={tokens}
              renderInput={({ InputLabelProps: _InputLabelProps, ...rest }) => (
                <TextField
                  sx={{ paddingLeft: 0 }}
                  {...rest}
                  autoComplete='false'
                  label={t('value')}
                  placeholder={valuePlaceholder}
                  value={filterField.value}
                />
              )}
              selectOnFocus
              value={filterField.value || null}
            />
          )
        ) : type === 'boolean' ? (
          <FilterBooleanField
            aria-label={displayName}
            onChange={value => onChange({ ...filterField, value: value ?? '' })}
            value={filterField.value as boolean}
          />
        ) : (
          <TextField
            disabled={isReadonly}
            label={t('value')}
            name={fieldName}
            onChange={({ currentTarget: { value } }) => onChange({ ...filterField, value })}
            placeholder={valuePlaceholder}
            sx={{ flex: 1 }}
            value={filterField.value || ''}
            {...(type === 'number' ? { type: 'number' } : {})}
          />
        ))}

      {!isReadonly && onRemove && (
        <Box gridColumn={columnCount} ml='auto' my='auto'>
          <Tooltip placement='left' title={t('filter.remove')}>
            <IconButton onClick={onRemove} size='small'>
              <Icon fontSize='inherit' name='close' />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {hasSubGroup && filterField.filter && (
        <CollectionFilterList>
          <FilterList
            filterDefinitionPath={[...filterDefinitionPath, filterDefinition]}
            filterDefs={
              fields.length ? fields : [{ ...filterDefinition, type: 'value', fields: [] }]
            }
            filterGroup={filterField.filter}
            nestingLevel={nestingLevel + 1}
            onChange={handleOnFiltersChange}
          />
        </CollectionFilterList>
      )}
    </>
  );
};

export default FilterInputs;
